import React, { useEffect, useMemo, useState } from "react";

const WIDGET_URL = "https://discord.com/api/guilds/1350390959938211850/widget.json";
const REFRESH_MS = 60_000;
const MAX_SHOW = 120;

const STATUS_LABELS = {
  online: "Online",
  idle: "Idle",
  dnd: "Do Not Disturb",
  streaming: "Streaming",
  invisible: "Invisible",
  offline: "Offline",
};

const EXCLUDE_IDS = new Set([
  "1351499824062664727",
  "184405311681986560",
  "1358778399149260990",
  "982257201211138050",
  "159985870458322944",
  "402528814548254720",
  "1354088626715103335",
]);

const EXCLUDE_NAME_PATTERNS = [
  "mee6",
  "chaobot",
  "chaotikmusicbot",
  "chaotikoverlord",
  "chaotikvc",
  "chaotikvcbot",
];

const DEFAULT_AVATAR = "https://cdn.discordapp.com/embed/avatars/1.png";

function extractVoiceMemberIds(channels) {
  if (!Array.isArray(channels)) {
    return [];
  }

  const ids = new Set();
  for (const channel of channels) {
    if (!Array.isArray(channel?.members)) continue;
    for (const member of channel.members) {
      const memberId = resolveId(member);
      if (memberId) {
        ids.add(memberId);
      }
    }
  }

  return [...ids];
}

function isCustomActivity(activity) {
  if (!activity) return false;
  const type = activity.type;
  if (typeof type === "string") {
    return type.toLowerCase() === "custom";
  }
  return Number(type) === 4;
}

function resolveActivity(member) {
  const activities = Array.isArray(member?.activities) ? member.activities : [];
  const customActivity = activities.find((activity) => isCustomActivity(activity));
  const primaryActivity =
    activities.find((activity) => !isCustomActivity(activity) && activity?.name) || null;

  const gameName =
    typeof member?.game === "string"
      ? member.game
      : typeof member?.game?.name === "string"
        ? member.game.name
        : "";

  const chipActivityName = primaryActivity?.name || gameName || "";

  const detailParts = [];
  if (primaryActivity?.details) detailParts.push(primaryActivity.details);
  if (primaryActivity?.state && primaryActivity.state !== primaryActivity?.details) {
    detailParts.push(primaryActivity.state);
  }
  const tooltipPrimary = [chipActivityName, detailParts.join(" • ").trim()]
    .filter(Boolean)
    .join(" — ");

  const customStatusText =
    typeof customActivity?.state === "string" && customActivity.state.trim().length > 0
      ? customActivity.state.trim()
      : typeof customActivity?.name === "string"
        ? customActivity.name
        : "";

  return {
    chipText: chipActivityName || customStatusText || "",
    tooltipText: tooltipPrimary || chipActivityName || "",
    customStatusText,
    customStatusEmoji: customActivity?.emoji || null,
  };
}

function normalizeStatus(status) {
  return (status || "offline").toString().toLowerCase();
}

function formatStatusLabel(status) {
  const key = normalizeStatus(status);
  return STATUS_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

function resolveEmojiData(emoji) {
  if (!emoji || typeof emoji !== "object") return null;
  if (emoji.id) {
    const format = emoji.animated ? "gif" : "png";
    return {
      type: "image",
      url: `https://cdn.discordapp.com/emojis/${emoji.id}.${format}?quality=lossless`,
      alt: emoji.name || "Emoji",
    };
  }
  if (emoji.name) {
    return { type: "text", value: emoji.name };
  }
  return null;
}

function resolveId(member) {
  if (!member) return "";
  if (member.id != null) return String(member.id);
  if (member.user && member.user.id != null) return String(member.user.id);
  const avatar = String(member.avatar_url || "");
  const match = avatar.match(/\/avatars\/(\d+)\//);
  return match && match[1] ? match[1] : "";
}

function normalizeName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function shouldExclude(member) {
  const id = resolveId(member);
  if (id && EXCLUDE_IDS.has(id)) return true;
  if (member?.bot === true) return true;

  const username = normalizeName(member?.username);
  const nickname = normalizeName(member?.nick);
  const hasBotSuffix = username.endsWith("bot") || nickname.endsWith("bot");

  return (
    hasBotSuffix ||
    EXCLUDE_NAME_PATTERNS.some((pattern) => username.includes(pattern) || nickname.includes(pattern))
  );
}

function filterMembers(members) {
  return [...members]
    .filter((member) => (member?.status || "").toLowerCase() !== "offline")
    .filter((member) => !shouldExclude(member));
}

function formatMembers(members) {
  return filterMembers(members)
    .sort((a, b) => (a?.nick || a?.username || "").localeCompare(b?.nick || b?.username || ""))
    .slice(0, MAX_SHOW);
}

export default function DiscordPresenceMarquee({
  className = "",
  autoFetch = true,
  membersOverride,
  channelsOverride,
  presenceCountOverride,
  hadErrorOverride,
}) {
  const [members, setMembers] = useState([]);
  const [presenceCount, setPresenceCount] = useState(0);
  const [hadError, setHadError] = useState(false);
  const [voiceMemberIds, setVoiceMemberIds] = useState([]);
  const [interactionPaused, setInteractionPaused] = useState(false);

  useEffect(() => {
    if (!autoFetch) {
      if (Array.isArray(membersOverride)) {
        setMembers(membersOverride);
      } else {
        setMembers([]);
      }
      setHadError(false);
    }
  }, [membersOverride, autoFetch]);

  useEffect(() => {
    if (!autoFetch) {
      if (Array.isArray(channelsOverride)) {
        setVoiceMemberIds(extractVoiceMemberIds(channelsOverride));
      } else {
        setVoiceMemberIds([]);
      }
    }
  }, [channelsOverride, autoFetch]);

  useEffect(() => {
    if (!autoFetch) {
      return undefined;
    }

    let isActive = true;

    async function load() {
      try {
        const response = await fetch(WIDGET_URL, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!isActive) return;

        setMembers(Array.isArray(data?.members) ? data.members : []);
        setPresenceCount(Number(data?.presence_count || 0));
        setVoiceMemberIds(extractVoiceMemberIds(data?.channels));
        setHadError(false);
      } catch (error) {
        if (!isActive) return;
        console.error("Discord widget fetch failed", error);
        setMembers([]);
        setPresenceCount(0);
        setVoiceMemberIds([]);
        setHadError(true);
      }
    }

    load();
    const interval = setInterval(load, REFRESH_MS);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [autoFetch]);

  const onlineMembers = useMemo(() => formatMembers(members), [members]);
  const visibleOnlineCount = useMemo(() => filterMembers(members).length, [members]);
  const count = Math.max(
    Number.isFinite(Number(presenceCountOverride))
      ? Math.min(Number(presenceCountOverride), visibleOnlineCount)
      : visibleOnlineCount,
    onlineMembers.length
  );
  const widgetHadError = typeof hadErrorOverride === "boolean" ? hadErrorOverride : hadError;
  const marqueeSpeed = Math.min(Math.max(onlineMembers.length * 1.35, 20), 48);
  const marqueeStyle = { "--discord-speed": `${marqueeSpeed}s` };
  const voiceMemberIdSet = useMemo(() => new Set(voiceMemberIds), [voiceMemberIds]);
  const chips = useMemo(() => {
    if (onlineMembers.length === 0) {
      return [];
    }

    const duplicated = [...onlineMembers, ...onlineMembers];
    return duplicated.map((member, index) => {
      const displayName = member?.nick || member?.username || "Member";
      const avatar = member?.avatar_url || DEFAULT_AVATAR;
      const memberId = resolveId(member) || `${displayName}-${index}`;
      const status = normalizeStatus(member?.status);
      const voiceActive = memberId ? voiceMemberIdSet.has(memberId) : false;
      const presence = resolveActivity(member);
      const tooltipStatusLabel = formatStatusLabel(status);
      const emojiData = resolveEmojiData(presence.customStatusEmoji);
      const chipActivityText = presence.chipText;
      const tooltipActivityText = presence.tooltipText;

      return (
        <div
          className={`discord-chip discord-status-${status} ${voiceActive ? "discord-chip-voice" : ""}`.trim()}
          key={`${memberId}-${index}`}
          onMouseEnter={() => setInteractionPaused(true)}
          onMouseLeave={() => setInteractionPaused(false)}
          onFocus={() => setInteractionPaused(true)}
          onBlur={() => setInteractionPaused(false)}
          tabIndex={0}
        >
          <img className="discord-avatar" src={avatar} alt="" loading="lazy" />
          <div className="discord-chip-body">
            <span className="discord-name">{displayName}</span>
            {chipActivityText ? <span className="discord-activity">{chipActivityText}</span> : null}
          </div>
          {voiceActive ? (
            <span className="discord-voice-icon" role="img" aria-label="In voice channel">
              🎙️
            </span>
          ) : null}
          <span className={`discord-dot status-${status}`} aria-hidden="true" />
          <div className="discord-tooltip" role="tooltip">
            <div className="discord-tooltip-name">{displayName}</div>
            <div className="discord-tooltip-status-row">
              <span className={`discord-status-indicator status-${status}`} aria-hidden="true" />
              <span className="discord-tooltip-status-label">{tooltipStatusLabel}</span>
              {voiceActive ? <span className="discord-tooltip-voice">In voice channel</span> : null}
            </div>
            {tooltipActivityText ? (
              <div className="discord-tooltip-activity">
                <span className="discord-tooltip-label">Activity</span>
                <span className="discord-tooltip-value">{tooltipActivityText}</span>
              </div>
            ) : null}
            {presence.customStatusText ? (
              <div className="discord-tooltip-custom">
                {emojiData ? (
                  emojiData.type === "image" ? (
                    <img
                      className="discord-tooltip-emoji"
                      src={emojiData.url}
                      alt={emojiData.alt}
                      loading="lazy"
                    />
                  ) : (
                    <span className="discord-tooltip-emoji" aria-hidden="true">
                      {emojiData.value}
                    </span>
                  )
                ) : null}
                <span className="discord-tooltip-value">{presence.customStatusText}</span>
              </div>
            ) : null}
          </div>
        </div>
      );
    });
  }, [onlineMembers, voiceMemberIdSet]);

  const showMarquee = onlineMembers.length > 0;

  return (
    <div
      className={`discord-widget flex min-w-0 items-center gap-3 overflow-hidden rounded-full bg-gray-900/70 px-4 py-2 text-sm text-white/80 backdrop-blur ${className}`.trim()}
    >
      <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-white">
        <span className="text-xs tracking-wide text-indigo-400">Users Online</span>
        <span className="inline-flex h-6 min-w-[2.5rem] items-center justify-center rounded-full bg-emerald-500/20 px-2 text-center text-xs font-semibold text-emerald-300">
          {count}
        </span>
      </span>
      <div className="discord-marquee-wrapper flex-1 min-w-0 w-full sm:ml-2">
        <div className="discord-marquee-frame">
          {showMarquee ? (
            <div className="discord-marquee" aria-live="polite">
              <div
                className={`discord-marquee-track ${interactionPaused ? "is-paused" : ""}`.trim()}
                style={marqueeStyle}
              >
                {chips}
              </div>
            </div>
          ) : (
            <div className="discord-marquee-empty">
              {widgetHadError ? "Widget unavailable" : "No members online"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
